
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabaseStorage } from '../../utils/supabaseStorage';

// Keep existing interfaces for compatibility
export interface User {
  id: number;
  username: string;
  email: string;
  password: string;
  role: 'admin' | 'doctor' | 'staff' | 'receptionist';
  permissions: string[];
  firstName: string;
  lastName: string;
  phone: string;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
}

export interface UserSession {
  user: User;
  token: string;
  expiresAt: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on app load
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('userToken');
        if (token) {
          const currentUser = validateUserSession(token);
          if (currentUser) {
            setUser(currentUser);
          } else {
            // Clear invalid session
            localStorage.removeItem('userSession');
            localStorage.removeItem('userToken');
            localStorage.removeItem('userData');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear potentially corrupted data
        localStorage.removeItem('userSession');
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('Attempting to authenticate user:', username);
      
      // Try Supabase authentication first
      const authenticatedUser = await supabaseStorage.authenticateUser(username, password);
      
      if (!authenticatedUser) {
        console.log('Authentication failed: Invalid credentials');
        return { success: false, error: 'Invalid username or password' };
      }

      if (!authenticatedUser.isActive) {
        console.log('Authentication failed: Account deactivated');
        return { success: false, error: 'Account is deactivated. Please contact administrator.' };
      }

      console.log('User authenticated successfully:', authenticatedUser.username);

      const session = createUserSession(authenticatedUser);
      localStorage.setItem('userSession', JSON.stringify(session));
      localStorage.setItem('userToken', session.token);
      localStorage.setItem('userData', JSON.stringify(authenticatedUser));
      
      setUser(authenticatedUser);
      console.log('Login successful, user set in state');
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      
      // Fallback to localStorage authentication if Supabase fails
      try {
        console.log('Falling back to localStorage authentication');
        const localAuthResult = await authenticateUserLocal(username, password);
        if (localAuthResult.success && localAuthResult.user) {
          const session = createUserSession(localAuthResult.user);
          localStorage.setItem('userSession', JSON.stringify(session));
          localStorage.setItem('userToken', session.token);
          localStorage.setItem('userData', JSON.stringify(localAuthResult.user));
          setUser(localAuthResult.user);
          return { success: true };
        }
      } catch (fallbackError) {
        console.error('Fallback authentication error:', fallbackError);
      }
      
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const logout = () => {
    console.log('Logging out user');
    localStorage.removeItem('userSession');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    sessionStorage.clear();
    setUser(null);
  };

  const checkPermission = (permission: string): boolean => {
    if (!user) return false;
    return hasPermission(user, permission);
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    hasPermission: checkPermission,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper functions (kept for compatibility)
const createUserSession = (user: User): UserSession => {
  const token = btoa(JSON.stringify({
    userId: user.id,
    timestamp: Date.now(),
    random: Math.random()
  }));
  
  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getHours() + 8); // 8 hours expiry
  
  return {
    user,
    token,
    expiresAt: expiresAt.toISOString()
  };
};

const validateUserSession = (token: string): User | null => {
  try {
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    if (session.token === token && new Date(session.expiresAt) > new Date()) {
      return session.user;
    }
  } catch (error) {
    console.error('Invalid session:', error);
  }
  return null;
};

const hasPermission = (user: User, permission: string): boolean => {
  if (user.role === 'admin' || user.permissions.includes('all')) {
    return true;
  }
  return user.permissions.includes(permission);
};

// Fallback local authentication
const authenticateUserLocal = async (username: string, password: string): Promise<{ success: boolean; user?: User }> => {
  const mockUsers = [
    {
      id: 1,
      username: 'admin',
      email: 'admin@homeoclinic.com',
      password: 'admin123',
      role: 'admin' as const,
      permissions: ['all'],
      firstName: 'Sarah',
      lastName: 'Wilson',
      phone: '+1 (555) 100-0001',
      isActive: true,
      createdAt: '2024-01-01T00:00:00Z',
      lastLogin: new Date().toISOString()
    },
    {
      id: 2,
      username: 'dr.smith',
      email: 'dr.smith@homeoclinic.com',
      password: 'doctor123',
      role: 'doctor' as const,
      permissions: ['patients.read', 'patients.write', 'prescriptions.read', 'prescriptions.write', 'records.read', 'records.write'],
      firstName: 'John',
      lastName: 'Smith',
      phone: '+1 (555) 100-0002',
      isActive: true,
      createdAt: '2024-01-02T00:00:00Z',
      lastLogin: new Date().toISOString()
    }
  ];

  const user = mockUsers.find(u => 
    (u.username === username || u.email === username) && 
    u.password === password && 
    u.isActive
  );
  
  if (user) {
    return { success: true, user };
  }
  
  return { success: false };
};
